import { reducerWithInitialState } from "typescript-fsa-reducers";
import range from "lodash/range";
import compact from "lodash/compact";

import { CompanyPostPin } from "../entities";

import {
  bootstrapCompanyPosts,
  loadCompanyPosts,
  bootstrapCompanyShow,
  loadCompanyShow,
  loadNextPosts,
  loadPinnablePosts,
  draftPostPinUpdated,
  saveDraftPostPins,
  sendCompanyPost,
  draftReset,
} from "../actions";
import { CompanyPostsResponse, CompanyShowResponse } from "../api";

export type DraftPostPin = Omit<CompanyPostPin, "id"> & { id: EntityId | null };

export interface PostsState {
  postIds: EntityId[];
  featuredPostIds: EntityId[];
  spaceIds: EntityId[];
  isLoaded: boolean;
  isLoadingNextPosts: boolean;
  currentPage: number;
  isAllPostsLoaded: boolean;
  pinnablePostIds: EntityId[];
  isPinnablePostsLoaded: boolean;
  isPinnablePostsLoading: boolean;
  postPinIds: EntityId[];
  latestPinnablePostIds: EntityId[];
  draftPostPins: DraftPostPin[];
  isDraftPostPinsDirty: boolean;
}

const InitialState: PostsState = {
  postIds: [],
  featuredPostIds: [],
  spaceIds: [],
  isLoaded: false,
  isLoadingNextPosts: false,
  currentPage: 1,
  isAllPostsLoaded: false,
  pinnablePostIds: [],
  isPinnablePostsLoaded: false,
  isPinnablePostsLoading: false,
  postPinIds: [],
  latestPinnablePostIds: [],
  draftPostPins: [],
  isDraftPostPinsDirty: false,
};

function initState(state: PostsState, { result, entities }: CompanyPostsResponse) {
  const draftPostPins = result.post_pins.map(p => entities.company_post_pins[p]!);
  const featuredPostIds = fillOutFeaturedPostIds(draftPostPins, result.latest_pinnable_posts);
  return {
    ...state,
    postIds: result.posts,
    spaceIds: result.spaces,
    postPinIds: result.post_pins,
    latestPinnablePostIds: result.latest_pinnable_posts,
    featuredPostIds,
    draftPostPins,
    isLoaded: true,
  };
}

function initStateFromBootstrapShow(state: PostsState, { result, entities }: CompanyShowResponse) {
  const draftPostPins = result.post_pins.map(p => entities.company_post_pins[p]!);
  const featuredPostIds = fillOutFeaturedPostIds(draftPostPins, result.latest_pinnable_posts);
  return {
    ...state,
    postPinIds: result.post_pins,
    latestPinnablePostIds: result.latest_pinnable_posts,
    featuredPostIds,
    draftPostPins,
  };
}

function fillOutFeaturedPostIds(draftPostPins: DraftPostPin[], pinnablePostIds: EntityId[]): EntityId[] {
  const pinnedPostIds = compact(draftPostPins.map(p => (p.post ? p.post : null)));
  if (pinnedPostIds.length === 3) {
    return pinnedPostIds;
  }

  const unPinnedPostIds = pinnablePostIds.filter(id => !pinnedPostIds.includes(id));
  if (pinnedPostIds.length === 0) {
    return unPinnedPostIds.slice(0, 3);
  }

  const postIds: EntityId[] = [];
  range(3).forEach(index => {
    const pin = draftPostPins.find(p => p.category_cd === index);
    if (pin && pin.post) {
      postIds.push(pin.post);
    } else {
      const latestPostId = unPinnedPostIds.find(id => !postIds.includes(id));
      if (latestPostId) {
        postIds.push(latestPostId);
      }
    }
  });
  return postIds;
}

const postsReducer = reducerWithInitialState<PostsState>(InitialState)
  .case(bootstrapCompanyPosts, initState)
  .case(loadCompanyPosts.done, (state, { result: actionRes }) => {
    return initState(state, actionRes);
  })
  .case(bootstrapCompanyShow, initStateFromBootstrapShow)
  .case(loadCompanyShow.done, (state, { result: actionRes }) => {
    return initStateFromBootstrapShow(state, actionRes);
  })
  .case(loadNextPosts.started, state => {
    return { ...state, isLoadingNextPosts: true };
  })
  .case(loadNextPosts.done, (state, { result }) => {
    return {
      ...state,
      isLoadingNextPosts: false,
      postIds: Array.from(new Set(state.postIds.concat(result.posts))), // Make postIds unique using Set
      currentPage: state.currentPage + 1,
      isAllPostsLoaded: result.meta.isLastPage,
    };
  })
  .case(loadNextPosts.failed, state => {
    return { ...state, isLoadingNextPosts: false };
  })
  .case(loadPinnablePosts.started, state => {
    return { ...state, isPinnablePostsLoading: true };
  })
  .case(loadPinnablePosts.done, (state, { result }) => {
    return {
      ...state,
      isPinnablePostsLoaded: true,
      isPinnablePostsLoading: false,
      pinnablePostIds: result.posts,
    };
  })
  .case(loadPinnablePosts.failed, state => {
    return { ...state, isPinnablePostsLoading: false };
  })
  .case(draftPostPinUpdated, (state, pin) => {
    const oldPin = state.draftPostPins.find(p => p.category_cd === pin.categoryCd);
    const newPin: DraftPostPin = {
      id: null,
      ...oldPin,
      company: pin.companyId,
      post: pin.postId,
      category_cd: pin.categoryCd,
    };
    const draftPostPins = state.draftPostPins
      .filter(p => p.category_cd !== newPin.category_cd)
      .concat(newPin)
      .sort((a, b) => a.category_cd - b.category_cd);
    const featuredPostIds = fillOutFeaturedPostIds(draftPostPins, state.pinnablePostIds);
    return {
      ...state,
      draftPostPins,
      featuredPostIds,
      isDraftPostPinsDirty: true,
    };
  })
  .case(saveDraftPostPins.done, (state, { result: pins }) => {
    // FIXME(KentoMoriwaki): Save postPinIds when saved
    return {
      ...state,
      postPinIds: pins.map(pin => pin.id),
      draftPostPins: pins,
      featuredPostIds: fillOutFeaturedPostIds(pins, state.latestPinnablePostIds),
      isDraftPostPinsDirty: false,
    };
  })
  .case(draftReset, (state, entities) => {
    const draftPostPins = state.postPinIds.map(p => entities.company_post_pins[p]!);
    const featuredPostIds = fillOutFeaturedPostIds(draftPostPins, state.latestPinnablePostIds);
    return {
      ...state,
      draftPostPins,
      featuredPostIds,
      isDraftPostPinsDirty: false,
    };
  })
  .case(sendCompanyPost.done, (state, { result }) => {
    return {
      ...state,
      postIds: [result.post, ...state.postIds],
    };
  });

export default postsReducer;



// WEBPACK FOOTER //
// ./frontend/src/companies/reducers/posts.ts